import { useCallback, useEffect, useRef, useState } from 'react';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import { LayerPaths, buildLayerUrl, useMapStore } from '@uofu-uss-gis/ui-react';
import * as UiReact from '@uofu-uss-gis/ui-react';
import { normalizeBuildingCode } from '../../utils/classUtils';

const isDebugEnabled = () => (
  typeof window !== 'undefined' && localStorage?.getItem('debugMyClassesPins') === 'true'
);
const debugLog = (...args: any[]) => {
  if (isDebugEnabled()) {
    console.warn('[useZoomToClassRoom DEBUG]', ...args);
  }
};

interface PendingRoomTarget {
  facilityId: string;
  roomNumber: string;
}

interface ZoomToClassRoomArgs {
  buildingCode: string;
  roomNumber: string;
}

interface LevelLike {
  levelId?: string;
  id?: string;
  name?: string;
  shortName?: string;
  attributes?: {
    LEVEL_ID?: string;
    level_short?: string;
    BldgLevel?: string;
    NAME?: string;
    NAME_SHORT?: string;
  };
}

const escapeSqlValue = (value: string) => value.replace(/'/g, "''");

const normalizeRoomToken = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

const getRoomTokenVariants = (value: string) => {
  const token = normalizeRoomToken(value);
  if (!token) {
    return [] as string[];
  }
  const variants = new Set([token]);
  variants.add(token.replace(/^([A-Z]*?)0+([0-9].*)$/, '$1$2'));
  variants.add(token.replace(/^0+([0-9].*)$/, '$1'));
  return Array.from(variants).filter(Boolean);
};

const getRoomCandidates = (roomNumber: string) => {
  const value = roomNumber.trim();
  if (!value) {
    return [] as string[];
  }
  return Array.from(new Set([
    value,
    value.toUpperCase(),
    value.replace(/\s+/g, ''),
  ].filter(Boolean)));
};

const getFacilityCandidates = (facilityId: string) => {
  const value = facilityId.trim();
  if (!value) {
    return [] as string[];
  }
  const numeric = String(Number(value));
  return Array.from(new Set([
    value,
    Number.isNaN(Number(value)) ? '' : numeric,
    value.padStart(5, '0'),
  ].filter(Boolean)));
};


const normalizeLevelToken = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');

const getLevelTokenVariants = (value: string) => {
  const token = normalizeLevelToken(value);
  if (!token) {
    return [] as string[];
  }
  const variants = new Set([token]);
  variants.add(token.replace(/^([A-Z]*?)0+([0-9].*)$/, '$1$2'));
  variants.add(token.replace(/^0+([0-9].*)$/, '$1'));
  return Array.from(variants).filter(Boolean);
};

const getLevelCandidates = (value: any) => {
  const candidates = [
    value?.levelId,
    value?.id,
    value?.name,
    value?.shortName,
    value?.attributes?.LEVEL_ID,
    value?.attributes?.level_short,
    value?.attributes?.BldgLevel,
    value?.attributes?.NAME,
    value?.attributes?.NAME_SHORT,
    value?.LEVEL_ID,
    value?.level_short,
    value?.BldgLevel,
  ]
    .map((v) => String(v ?? '').trim())
    .filter(Boolean);

  const tokens = new Set<string>();
  candidates.forEach((candidate) => {
    getLevelTokenVariants(candidate).forEach((token) => tokens.add(token));
  });
  return Array.from(tokens);
};

const resolveLevelIndex = (availableLevels: LevelLike[], target: any) => {
  const targetTokens = getLevelCandidates(target);
  if (!targetTokens.length) {
    return -1;
  }
  return availableLevels.findIndex((level) => {
    const levelTokens = getLevelCandidates(level);
    return levelTokens.some((token) => targetTokens.includes(token));
  });
};

const useZoomToClassRoom = () => {
  const { mapView, restEndpoint } = useMapStore();
  const useIndoorsStore = (UiReact as any).useIndoorsStore as undefined | (() => {
    availableLevels: LevelLike[];
    activeLevelIndex: number;
    setActiveLevelIndex: (index: number) => void;
    setSelectedFacility: (facilityId: string) => void;
  });
  const indoorsServiceUrl = (UiReact as any).INDOORS_SERVICE_URL as string | undefined;
  const indoorsSubLayerIndex = (UiReact as any).IndoorsSubLayerIndex as { Units?: number; Levels?: number } | undefined;

  const indoorsState = useIndoorsStore?.();
  const {
    availableLevels = [],
    activeLevelIndex = 0,
    setActiveLevelIndex = () => undefined,
    setSelectedFacility = () => undefined,
  } = indoorsState ?? {};
  const pendingRoomRef = useRef<PendingRoomTarget | null>(null);
  const [pendingRoomRequestId, setPendingRoomRequestId] = useState(0);

  // Tracks the level index we want active. The indoors plugin in
  // @uofu-uss-gis/ui-react resets activeLevelIndex to the ground floor any
  // time it (re)loads levels for a facility — and that load runs in parallel
  // with our own room/level resolution. If its setActiveLevelIndex(0) lands
  // AFTER ours, the user ends up on level 1 even though we resolved level 2.
  //
  // We solve this with a small reconciliation window: after we compute the
  // target level we record it (and an expiry time) here, then a separate
  // effect below re-applies the desired index whenever the store diverges
  // until either it sticks or the window closes.
  const desiredLevelIndexRef = useRef<number | null>(null);
  const desiredLevelExpiresAtRef = useRef<number>(0);
  const DESIRED_LEVEL_WINDOW_MS = 5000;

  useEffect(() => {
    const pendingRoom = pendingRoomRef.current;
    if (!pendingRoom || !mapView || !availableLevels.length) {
      return;
    }

    let cancelled = false;
    pendingRoomRef.current = null;

    if (!indoorsServiceUrl || indoorsSubLayerIndex?.Units === undefined) {
      return;
    }

    const unitsLayer = new FeatureLayer({
      url: `${indoorsServiceUrl}/${indoorsSubLayerIndex.Units}`,
      outFields: ['LEVEL_ID', 'level_short', 'BldgLevel', 'NAME', 'NAME_LONG', 'UNIT_ID', 'sp_loc_code', 'gis_loc_code', 'bldg_num'],
    });

    const roomCandidates = getRoomCandidates(pendingRoom.roomNumber);
    const facilityCandidates = getFacilityCandidates(pendingRoom.facilityId);
    if (!roomCandidates.length || !facilityCandidates.length) {
      return;
    }

    // Use bldg_num field (integer) instead of FACILITY_ID which doesn't exist
    // Convert facility candidates to numeric building numbers
    const bldgNums = Array.from(new Set(facilityCandidates.map(id => Number(id)))).map(id => {
      const num = Number(id);
      return Number.isNaN(num) ? null : num;
    }).filter((n): n is number => n !== null);

    if (!bldgNums.length) {
      return;
    }

    const facilityWhere = bldgNums.map(bldgNum => `bldg_num = ${bldgNum}`).join(' OR ');

    const query = unitsLayer.createQuery();
    query.where = `(${facilityWhere})`;
    query.returnGeometry = true;

    unitsLayer.queryFeatures(query).then(async (result) => {
      if (cancelled || !result.features.length) {
        return;
      }

      const roomTokens = roomCandidates.flatMap((candidate) => getRoomTokenVariants(candidate));
      const unit = result.features.find((feature: any) => {
        const names = [
          feature?.attributes?.NAME,
          feature?.attributes?.NAME_LONG,
          feature?.attributes?.UNIT_ID,
          feature?.attributes?.sp_loc_code,
          feature?.attributes?.gis_loc_code,
        ]
          .flatMap((value: unknown) => getRoomTokenVariants(String(value ?? '')))
          .filter(Boolean);
        return names.some((token) => roomTokens.includes(token));
      }) ?? result.features[0];

      let levelTarget: any = unit?.attributes ?? {};
      if (indoorsSubLayerIndex?.Levels !== undefined) {
        const levelsLayer = new FeatureLayer({
          url: `${indoorsServiceUrl}/${indoorsSubLayerIndex.Levels}`,
          outFields: ['FACILITY_ID', 'LEVEL_ID', 'NAME', 'NAME_SHORT', 'LEVEL_NUMBER', 'VERTICAL_ORDER'],
        });
        const levelsQuery = levelsLayer.createQuery();
        const facilityValues = Array.from(new Set(bldgNums.map((value) => String(value))));
        levelsQuery.where = facilityValues.map((value) => `FACILITY_ID = '${value}'`).join(' OR ');
        levelsQuery.returnGeometry = false;

        await levelsLayer.queryFeatures(levelsQuery).then((levelsResult) => {
          const unitLevelId = String(unit?.attributes?.LEVEL_ID ?? '').trim();
          debugLog(`Levels query returned ${levelsResult.features.length} rows for facilities=${facilityValues.join(',')}`);
          const matchingLevel = levelsResult.features.find((levelFeature: any) => {
            const levelId = String(levelFeature?.attributes?.LEVEL_ID ?? '').trim();
            return Boolean(levelId && unitLevelId && levelId === unitLevelId);
          });
          if (matchingLevel?.attributes) {
            debugLog(`Matched level metadata for unit levelId=${unitLevelId}`, matchingLevel.attributes);
            levelTarget = {
              ...(unit?.attributes ?? {}),
              ...matchingLevel.attributes,
            };
          }
        }).catch((error) => {
          console.error('Error querying indoors levels for room zoom:', error);
        });
      }

      const levelIndex = resolveLevelIndex(availableLevels, levelTarget);
      debugLog(
        `Resolved levelIndex=${levelIndex} for room=${pendingRoom.roomNumber}`,
        {
          levelTarget,
          availableLevels: availableLevels.map((level) => ({
            levelId: level.levelId ?? level.id ?? level.attributes?.LEVEL_ID,
            attributes: level.attributes,
          })),
        }
      );
      if (levelIndex >= 0) {
        // Record the desired index so the reconciliation effect below can
        // re-apply it if the indoors plugin's own level loader overwrites
        // it. The plugin batches setAvailableLevels + setActiveLevelIndex(0)
        // when its FACILITY_ID levels query resolves; that resolution can
        // race with our own queries here.
        desiredLevelIndexRef.current = levelIndex;
        desiredLevelExpiresAtRef.current = Date.now() + DESIRED_LEVEL_WINDOW_MS;
        setActiveLevelIndex(levelIndex);
      }

      if (unit.geometry) {
        void mapView.goTo({ target: unit.geometry, zoom: 21 }, { duration: 400 });
      }
    }).catch((error) => {
      console.error('Error zooming to class room:', error);
    });

    return () => {
      cancelled = true;
    };
  }, [
    availableLevels,
    indoorsServiceUrl,
    indoorsSubLayerIndex?.Units,
    indoorsSubLayerIndex?.Levels,
    mapView,
    pendingRoomRequestId,
    setActiveLevelIndex,
  ]);

  // Reconciliation: if we've recorded a desired level index, re-apply it any
  // time the store diverges (within the time window). This wins the race
  // against the indoors plugin's own setActiveLevelIndex(groundIdx) call
  // that fires when it finishes loading levels for the facility.
  //
  // IMPORTANT: We do NOT clear the desired marker on first successful match.
  // The plugin's stray setActiveLevelIndex(0) can arrive AFTER our setting
  // wins, so we have to keep defending throughout the window.
  useEffect(() => {
    const desired = desiredLevelIndexRef.current;
    if (desired === null) {
      return;
    }
    if (Date.now() > desiredLevelExpiresAtRef.current) {
      desiredLevelIndexRef.current = null;
      return;
    }
    if (desired < 0 || desired >= availableLevels.length) {
      return;
    }
    if (activeLevelIndex === desired) {
      // Already matches — nothing to do this tick, but keep the marker
      // armed so a late plugin overwrite gets corrected.
      return;
    }
    debugLog(
      `Reapplying desired level index=${desired} (current=${activeLevelIndex})`,
    );
    setActiveLevelIndex(desired);
  }, [activeLevelIndex, availableLevels, setActiveLevelIndex]);

  const zoomToClassRoom = useCallback(async ({ buildingCode, roomNumber }: ZoomToClassRoomArgs) => {
    if (!mapView || !restEndpoint || !roomNumber) {
      return false;
    }

    const normalizedBuildingCode = normalizeBuildingCode(buildingCode.trim());
    if (!normalizedBuildingCode || !useIndoorsStore) {
      return false;
    }

    const structureLayer = new FeatureLayer({
      url: buildLayerUrl(restEndpoint, LayerPaths.structures),
      outFields: ['building_number', 'ABBREVIATION'],
    });

    const escapedBldgCd = escapeSqlValue(normalizedBuildingCode);
    const query = structureLayer.createQuery();
    query.where = `ABBREVIATION = '${escapedBldgCd}'`;
    query.outFields = ['building_number', 'ABBREVIATION'];
    query.returnGeometry = false;

    try {
      const result = await structureLayer.queryFeatures(query);
      const feature = result.features[0];
      const facilityId = feature?.attributes?.building_number;
      if (!facilityId) {
        return false;
      }

      pendingRoomRef.current = {
        facilityId: String(facilityId),
        roomNumber: roomNumber.trim(),
      };
      // A new selection cancels any in-flight reconciliation from a
      // previous selection so we don't fight the user.
      desiredLevelIndexRef.current = null;
      desiredLevelExpiresAtRef.current = 0;
      setPendingRoomRequestId((previous) => previous + 1);
      setSelectedFacility(String(facilityId));
      return true;
    } catch (error) {
      console.error('Error preparing class room zoom:', error);
      return false;
    }
  }, [mapView, restEndpoint, setSelectedFacility]);

  return { zoomToClassRoom };
};

export default useZoomToClassRoom;
